/**
 * Benvel variant-aware gallery wrapper. Story 4.3.
 *
 * Purpose: react to variant changes on the PDP and refresh the gallery via
 * SRA endpoint #4 (`/products/{handle}?section_id=product-information&variant={id}`).
 * See `_bmad-output/implementation-artifacts/4-3-variant-aware-gallery.md`
 * and `design/contracts/pdp.md` § "Variant-aware gallery contract — Story 4.3".
 *
 * Coordinates with — but does NOT modify — Horizon's `<media-gallery>` web
 * component (`assets/media-gallery.js`). The Benvel wrapper sits AROUND
 * `<media-gallery>` and triggers an SRA section swap; Horizon's inner
 * `#handleVariantUpdate` consumes the already-fetched HTML.
 *
 * Listens (on closest `.shopify-section`):
 *   - `benvel:variant-changed` — Benvel picker (Story 4.4) detail.variant
 *   - `benvel:section-rendered` — post-SRA-swap signal from
 *     `benvel-section-renderer-helpers.js` (Story 1.6).
 * Listens (on `window`):
 *   - `popstate` — back/forward nav with `?variant=` changes.
 *
 * Emits (on the element):
 *   - `benvel:gallery-variant-rendered` — { variantId, mediaCount,
 *     renderDurationMs, source } — fired once per SRA swap that targets the
 *     `product-information` section.
 *
 * Web-component conventions (project-context §22): vanilla, `customElements.get/
 * define` guard, `AbortController` lifecycle, no framework. ES module imports
 * are permitted (importmap exposes `@theme/benvel-section-renderer-helpers`).
 */

import { swapSection } from '@theme/benvel-section-renderer-helpers';

const SECTION_ID = 'product-information';
const SOURCE_DEFAULT = 'benvel-variant-gallery';

class BenvelVariantGallery extends HTMLElement {
  #controller = new AbortController();
  #swapStartTime = 0;
  #pendingVariantId = null;
  #pendingSource = SOURCE_DEFAULT;

  connectedCallback() {
    if (this._mounted) return;
    this._mounted = true;

    const { signal } = this.#controller;
    const sectionEl = this.closest('.shopify-section');

    sectionEl?.addEventListener(
      'benvel:variant-changed',
      this.#handleVariantChanged,
      { signal }
    );
    document.addEventListener(
      'benvel:section-rendered',
      this.#handleSectionRendered,
      { signal }
    );
    window.addEventListener('popstate', this.#handlePopstate, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
    this._mounted = false;
  }

  #handleVariantChanged = (event) => {
    const detail = event?.detail || {};
    const variantId = this.#resolveVariantId(detail);
    if (!variantId) return;

    const previousVariantId = this.dataset.activeVariantId || null;
    if (String(variantId) === String(previousVariantId)) return;

    const source = detail.source || SOURCE_DEFAULT;
    this.#triggerSwap(variantId, source);
  };

  #handlePopstate = () => {
    const variantId = this.#readVariantIdFromUrl(window.location.href);
    if (!variantId) return;
    if (String(variantId) === String(this.dataset.activeVariantId || '')) return;
    this.#triggerSwap(variantId, 'popstate', { pushHistory: false });
  };

  #handleSectionRendered = (event) => {
    const detail = event?.detail || {};
    if (detail.sectionId !== SECTION_ID) return;
    if (this.#pendingVariantId == null) return;

    const renderDurationMs =
      this.#swapStartTime > 0
        ? Math.max(0, Math.round(performance.now() - this.#swapStartTime))
        : 0;

    const variantId = this.#pendingVariantId;
    const source = this.#pendingSource;
    this.#pendingVariantId = null;
    this.#pendingSource = SOURCE_DEFAULT;
    this.#swapStartTime = 0;

    // The Benvel WC instance was likely re-marked for remount by the swap
    // helper. The currently-live instance is the new one inserted by the morph;
    // it dispatches the event on itself so any subscriber listening on the
    // section or document receives it.
    const liveInstance =
      document.querySelector(
        `benvel-variant-gallery[data-product-handle="${this.dataset.productHandle}"]`
      ) || this;

    if (liveInstance instanceof BenvelVariantGallery) {
      liveInstance.dataset.activeVariantId = String(variantId);
    }

    const mediaCount =
      liveInstance.querySelectorAll('[data-bv-media-id]').length || 0;

    liveInstance.dispatchEvent(
      new CustomEvent('benvel:gallery-variant-rendered', {
        bubbles: true,
        composed: true,
        detail: {
          variantId: String(variantId),
          mediaCount,
          renderDurationMs,
          source,
        },
      })
    );
  };

  #triggerSwap(variantId, source, options = {}) {
    const { pushHistory = true } = options;

    this.#swapStartTime = performance.now();
    this.#pendingVariantId = String(variantId);
    this.#pendingSource = source || SOURCE_DEFAULT;

    const sectionEl = this.closest('.shopify-section');
    const sectionId = sectionEl?.id
      ? sectionEl.id.replace(/^shopify-section-/, '')
      : SECTION_ID;

    const swapUrl = this.#buildSwapUrl(variantId);

    if (pushHistory) {
      try {
        const browserUrl = this.#buildBrowserUrl(variantId);
        history.pushState({ variantId: String(variantId) }, '', browserUrl);
      } catch (err) {
        // History push is best-effort; SRA swap should still proceed.
      }
    }

    swapSection(sectionId, {
      url: swapUrl,
      source: this.#pendingSource,
      restoreScrollPolicy: 'preserve',
    }).catch(() => {
      // `swapSection` already dispatches `benvel:network-error`; no rethrow.
      this.#pendingVariantId = null;
      this.#pendingSource = SOURCE_DEFAULT;
      this.#swapStartTime = 0;
    });
  }

  #buildSwapUrl(variantId) {
    const handle = this.dataset.productHandle;
    if (!handle) return undefined;
    const url = new URL(`/products/${handle}`, window.location.origin);
    url.searchParams.set('variant', String(variantId));
    return url;
  }

  #buildBrowserUrl(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variantId));
    return url.toString();
  }

  #readVariantIdFromUrl(href) {
    try {
      const url = new URL(href);
      return url.searchParams.get('variant');
    } catch {
      return null;
    }
  }

  #resolveVariantId(detail) {
    if (detail == null) return null;
    if (detail.variantId != null) return detail.variantId;
    if (detail.variant && detail.variant.id != null) return detail.variant.id;
    return null;
  }
}

if (!customElements.get('benvel-variant-gallery')) {
  customElements.define('benvel-variant-gallery', BenvelVariantGallery);
}
